import {Auth, Hub} from 'aws-amplify';
import ServerData from '../serverData';
import CacheData from './cacheData';
import {BllinkLogger} from './bllink_loggers';
import {isMobileView} from './isMobileView';

/**
 * where a manager lands right after logging in.
 *
 * the decision is made at the login itself and nowhere else, so no page has to undo a landing it
 * has already started rendering.
 * */

/**
 * the mobile manager app is served from /m on the same origin, so the cognito session carries over
 * and no second login is needed
 * */
const MOBILE_APP_URL = '/m/';

const SOCIAL_LOGIN_PENDING_KEY = 'socialLoginPending';

/**
 * TEMPORARY - the mobile app is opening up partner by partner and will end up open to every manager.
 * when it does, drop both lists together with the two functions under them: the whole decision
 * collapses into `isMobileView()`, and nothing else here has to change.
 *
 * two lists, because which one a partner belongs in is not a style choice - it is where that
 * partner can be read from at all.
 * */

/**
 * real management companies, spelled the way the profile reports them: the partner's externalID.
 * case does not matter, paste them however the admin screens show them.
 * */
const MOBILE_APP_PARTNER_EXTERNAL_IDS = [];

/**
 * partners matched on the building instead, by internal numeric id.
 *
 * partner 47 can only be matched this way. a user record is never allowed to point at it - Users
 * has a beforeCreate/beforeUpdate hook that throws on `partnerID === 47` - so it cannot appear in
 * a profile at all, under any spelling. it sits on the building, through PartnerAccounts, and that
 * is the only place it can be read from.
 * */
const MOBILE_APP_PARTNER_INTERNAL_IDS = [47];

/**
 * the partner of the building this manager manages.
 *
 * only asked when the profile names no partner, which is what an individual manager looks like:
 * their partner is on the building, not on their user record. one building belongs to exactly one
 * partner - PartnerAccounts is unique on ptopID - so this is a lookup, not a guess.
 *
 * asks for the building without its stats and its tenants. those are the expensive half of that
 * endpoint, and the partner is resolved separately from them server side, so dropping them costs
 * us nothing here.
 *
 * the manager's first building stands for the rest. an individual manager has one, which is the
 * case this exists for.
 *
 * @param {ServerData} client
 * @return {Promise<number|null>} internal partner id
 * */
async function getBuildingPartnerID(client) {
    const {buildings} = await client.managers.buildings.getAllManagerBuildings();
    const buildingID = buildings?.[0]?.buildingID;
    if (!buildingID) {
        return null;
    }
    const {building} = await client.managers.buildings.getManagerBuilding(buildingID, {
        skipStats: true,
        skipTenantDetails: true,
        skipDisabledCheck: true,
    });
    return building?.partnerID ?? null;
}

/**
 * @param {ServerData} client
 * @param {{userID?: string, partnerID?: string}} profile
 * */
async function shouldUseMobileApp(client, profile) {
    if (!isMobileView()) {
        return false;
    }
    // a profile with no userID is not a manager the server recognises
    if (!profile?.userID) {
        return false;
    }
    // a real management company is named in the profile already, and costs nothing more to check
    if (profile.partnerID) {
        const partnerID = profile.partnerID.trim().toLowerCase();
        return MOBILE_APP_PARTNER_EXTERNAL_IDS.some(id => id.trim().toLowerCase() === partnerID);
    }
    // no partner on the user record. that is either an individual manager, whose partner is on the
    // building, or someone holding a building permission without belonging to a company. only the
    // building tells those two apart - the profile cannot
    if (MOBILE_APP_PARTNER_INTERNAL_IDS.length === 0) {
        return false;
    }
    return MOBILE_APP_PARTNER_INTERNAL_IDS.includes(await getBuildingPartnerID(client));
}

/**
 * asked here, with the token the login just issued, instead of waiting for the manager page to ask
 * for it later.
 *
 * the answer goes into the same cache key getUserProfile reads (5 hours), so that later fetch is
 * served from cache and never reaches the server. we always go to the server here rather than
 * through getOrCache, because the cached profile may still belong to the manager who logged out.
 *
 * the building lookup next to it gets no such treatment on purpose: nothing caches the building
 * endpoint on the client, and adding that would stale the money figures on the building dashboard,
 * which are refreshed deliberately after a payment. it is kept cheap instead, not cached.
 *
 * @param {ServerData} client
 * */
async function getManagerProfile(client) {
    const profile = await client.managers.getProfile();
    CacheData.cache('profile', profile);
    return profile;
}

/**
 * the single "where does this manager go now that they are logged in" decision, including the
 * redirect itself.
 *
 * @param {string} accessToken token issued by the login that just succeeded
 * @param {string|null} defaultUrl where this login would have gone on its own. null means the
 *        manager is already on that page (social login comes back to it), so only a handoff to the
 *        mobile app is worth a navigation.
 * @return {Promise<boolean>} true when the manager was sent to the mobile app
 * */
export async function goToPostLoginPage(accessToken, defaultUrl) {
    let url = defaultUrl;
    let usedMobileApp = false;

    try {
        const client = new ServerData(null, null, {accessToken: accessToken});
        const profile = await getManagerProfile(client);
        if (await shouldUseMobileApp(client, profile)) {
            url = MOBILE_APP_URL;
            usedMobileApp = true;
        }
    } catch (e) {
        // the landing page fetches the profile itself, so failing here costs the mobile app handoff
        // and nothing more - the manager still lands where this login was going anyway
        BllinkLogger.error('could not decide the post login landing page', e);
    }

    if (url) {
        window.location.href = url;
    }
    return usedMobileApp;
}

/**
 * social login hands the browser to cognito, so there is no moment to decide anything before
 * leaving. this flag is what lets us recognise the way back.
 * */
export function markSocialLoginPending() {
    window.localStorage.setItem(SOCIAL_LOGIN_PENDING_KEY, 'true');
}

async function handleSocialLoginReturn() {
    if (window.localStorage.getItem(SOCIAL_LOGIN_PENDING_KEY) !== 'true') {
        return;
    }
    // taken before the await so the boot call and the amplify event below cannot both act on it
    window.localStorage.removeItem(SOCIAL_LOGIN_PENDING_KEY);

    let accessToken;
    try {
        const user = await Auth.currentAuthenticatedUser();
        accessToken = user.signInUserSession.accessToken.jwtToken;
    } catch (e) {
        // amplify has not finished with the cognito redirect yet, or the manager walked away from
        // the cognito page and came back on their own. put the flag back: if it was the first, the
        // amplify event below runs this again, and if it was the second nothing else will.
        markSocialLoginPending();
        return;
    }

    // cognito sends the manager to the all buildings page, which is where they already are, so
    // there is no default url to navigate to here
    await goToPostLoginPage(accessToken, null);
}

/**
 * call once when the app boots. does nothing unless the manager is coming back from a social login.
 * @return {function} unsubscribe
 * */
export function watchForSocialLoginReturn() {
    // registered first so it catches a login that completes while the call below is still waiting
    const stopListening = Hub.listen('auth', ({payload}) => {
        if (payload?.event === 'signIn' || payload?.event === 'cognitoHostedUI') {
            handleSocialLoginReturn();
        }
    });
    handleSocialLoginReturn();
    return stopListening;
}
